import React, { useState, useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import { useQuery } from '@apollo/client';
import { track } from 'core/helpers/analytics';
import { isBranchEnv } from 'brand-config';

import { AuthContext } from 'core/components/auth/auth.context';
import { Label } from 'core/components/label';
import { Card } from 'core/components/card';
import { Spinner } from 'core/components/spinner';
import { policyType } from '@ourbranch/lookups';
import celebrateImg from 'core/assets/svg/celebrate.svg';
import imgAlreadySpun from 'core/assets/svg/imgAlreadySpun.svg';
import { GET_SPIN_THE_WHEEL_ELIGIBILITY, GET_SPIN_THE_WHEEL_RESULT } from 'core/store/offer-store/offer-queries';
import CustomSpinWheel from './custom-spin-wheel';
import useStyles from './purchased.styles';

// Policy types that get the bundle spinner
const BUNDLE_TYPES = [policyType.HABundle];

// Policy types that get the monoline spinner
const MONOLINE_SPINNER_TYPES = [
  policyType.ARBundle,
  policyType.CABundle,
  policyType.Auto,
  policyType.Home,
  policyType.AutoBundle,
  policyType.HomeBundle
];

// All policy types eligible for a spinner (bundle + monoline)
const SPINNER_ELIGIBLE_TYPES = [...BUNDLE_TYPES, ...MONOLINE_SPINNER_TYPES];

const SpinTheWheel = ({ offerId, offerStore }) => {
  const classes = useStyles();
  const { user, isAgency } = useContext(AuthContext);
  const [spinComplete, setSpinComplete] = useState(null); // null = not spun, number = winning amount
  const trackedShown = useRef(false);

  const hasEligiblePolicyType = SPINNER_ELIGIBLE_TYPES.includes(offerStore.selectedOption);
  const isBundle = BUNDLE_TYPES.includes(offerStore.selectedOption);

  const { data: eligibilityData, loading: eligibilityLoading } = useQuery(GET_SPIN_THE_WHEEL_ELIGIBILITY, {
    variables: { email: user?.email },
    skip: !isAgency || !user?.email
  });
  const isEligible = isAgency && eligibilityData?.getSpinTheWheelEligibility?.eligible;

  const { data: spinResultData, loading: spinResultLoading } = useQuery(GET_SPIN_THE_WHEEL_RESULT, {
    variables: { offerId },
    skip: !isEligible
  });
  const alreadySpunForOffer = Boolean(spinResultData?.getSpinTheWheelResult?.offerId);
  const shouldShowSpinner =
    isAgency && hasEligiblePolicyType && isEligible && !alreadySpunForOffer && spinComplete === null;

  // Track spinner shown — fire once when the active spinner will render
  useEffect(() => {
    if (shouldShowSpinner && !trackedShown.current) {
      trackedShown.current = true;
      try {
        track('Spin The Wheel Shown', {
          offerId,
          isBundle,
          selectedOption: offerStore.selectedOption,
          rep: user?.username
        });
      } catch {
        // tracking failure should not affect UI
      }
    }
  }, [shouldShowSpinner, offerId, isBundle, offerStore.selectedOption, user?.username]);

  // Show nothing if not Branch brand, not an agency user, or if policy type doesn't qualify
  if (!isBranchEnv || !isAgency || !hasEligiblePolicyType) {
    return null;
  }

  // Show loader while checking eligibility and spin history
  const isLoading = eligibilityLoading || spinResultLoading;
  if (isLoading) {
    return (
      <Card className={classes.spinWheelCard}>
        <Grid
          container
          direction="column"
          alignItems="center"
          justifyContent="center"
          className={classes.spinResultCard}
        >
          <div className={classes.spinLoadingSpinner}>
            <Spinner size={96} />
          </div>
        </Grid>
      </Card>
    );
  }

  // Not eligible
  if (!isEligible) {
    return null;
  }

  // Already spun for this offer (detected on page load)
  if (alreadySpunForOffer && spinComplete === null) {
    return (
      <Card className={classes.spinWheelCard}>
        <Grid
          container
          direction="column"
          alignItems="center"
          justifyContent="center"
          className={classes.spinResultCard}
        >
          <img src={imgAlreadySpun} alt="Already spun" className={classes.spinResultImage} />
          <Label type="subtitle1">You've already used your spin for this offer.</Label>
          <Label type="greenSmall" className={classes.spinResultSubtitle}>
            Sell another home or auto policy to earn your next one.
          </Label>
        </Grid>
      </Card>
    );
  }

  // Just completed a spin — show "You're all set"
  if (spinComplete !== null) {
    return (
      <Card className={classes.spinWheelCard}>
        <Grid
          container
          direction="column"
          alignItems="center"
          justifyContent="center"
          className={classes.spinResultCard}
        >
          <img src={celebrateImg} alt="Celebration" className={classes.spinResultImage} />
          <Label type="subtitle1">You're all set!</Label>
          <Label type="greenSmall" className={classes.spinResultSubtitle}>
            You've earned <strong>${spinComplete}</strong>. We've sent reward details to {user?.email}. Check your inbox
            for next steps.
          </Label>
          <Label type="greenSmall" className={classes.spinResultSubtitle}>
            {isBundle
              ? 'Keep selling home & auto to earn more spins.'
              : 'Bundle home & auto next time to spin for up to $200.'}
          </Label>
        </Grid>
      </Card>
    );
  }

  // Active spinner
  return (
    <Card className={classes.spinWheelCard}>
      <CustomSpinWheel isBundle={isBundle} offerId={offerId} onSpinComplete={(amount) => setSpinComplete(amount)} />
    </Card>
  );
};

SpinTheWheel.propTypes = {
  offerId: PropTypes.string.isRequired,
  offerStore: PropTypes.object.isRequired
};

export default SpinTheWheel;
